import { useState, useEffect, useCallback } from "react";
import { Plug, Play, CheckCircle2, AlertCircle, Clock, Loader2, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";
import { toast } from "sonner";

const aniviewApi = {
  getSettings: () => api.get("/admin/aniview/settings"),
  updateSettings: (data) => api.put("/admin/aniview/settings", data),
  testConnection: () => api.post("/admin/aniview/test-connection"),
  sync: (data) => api.post("/admin/aniview/sync", data),
  getSyncLogs: () => api.get("/admin/aniview/sync-logs"),
};

export default function AdvertisementsPage() {
  const [settings, setSettings] = useState({ email: "", account_id: "", password_set: false, api_key_set: false, daily_sync_enabled: false, daily_sync_time: "10:00" });
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [accountId, setAccountId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [dailySyncEnabled, setDailySyncEnabled] = useState(false);
  const [dailySyncTime, setDailySyncTime] = useState("10:00");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connected, setConnected] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [logs, setLogs] = useState([]);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await aniviewApi.getSettings();
      setSettings(res.data);
      setEmail(res.data.email || "");
      setAccountId(res.data.account_id || "");
      setDailySyncEnabled(res.data.daily_sync_enabled || false);
      setDailySyncTime(res.data.daily_sync_time || "10:00");
    } catch {}
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await aniviewApi.getSyncLogs();
      setLogs(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchSettings(); fetchLogs(); }, [fetchSettings, fetchLogs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { email, account_id: accountId, daily_sync_enabled: dailySyncEnabled, daily_sync_time: dailySyncTime };
      if (password) data.password = password;
      if (apiKey) data.api_key = apiKey;
      await aniviewApi.updateSettings(data);
      toast.success("Settings saved");
      setPassword("");
      setApiKey("");
      fetchSettings();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setConnected(null);
    try {
      await aniviewApi.testConnection();
      setConnected(true);
      toast.success("Connection successful");
    } catch (err) {
      setConnected(false);
      toast.error(err.response?.data?.detail || "Connection failed");
    }
    setTesting(false);
  };

  const handleSync = async () => {
    if (!startDate || !endDate) { toast.error("Select start and end dates"); return; }
    setSyncing(true);
    try {
      const res = await aniviewApi.sync({ start_date: startDate, end_date: endDate });
      toast.success(`Synced: ${res.data.rows_inserted} inserted, ${res.data.rows_updated} updated`);
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    }
    setSyncing(false);
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="advertisements-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          FAST TV Ads
        </h1>
        <p className="text-sm text-slate-500 mt-1">Manage Aniview ad server connection and sync revenue data</p>
      </div>

      {/* Credentials */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="aniview-credentials">
        <h3 className="text-sm font-semibold text-slate-900">Aniview Credentials</h3>
        <div className="space-y-3">
          {/* API Key (primary method) */}
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">API Key {settings.api_key_set && <span className="text-emerald-500 text-[10px]">(set)</span>}</Label>
            <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={settings.api_key_set ? "Leave blank to keep current" : "Enter your Aniview API key"} className="h-9 text-sm max-w-md" data-testid="aniview-api-key" />
            <p className="text-[11px] text-slate-400 mt-1">If you have an API key, it will be used directly. Otherwise, email/password login is used.</p>
          </div>
          {/* Email / Account ID / Password fallback */}
          <details className="group">
            <summary className="text-xs font-medium text-slate-500 cursor-pointer hover:text-slate-700 select-none">Email/Password Login (alternative)</summary>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Email</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" className="h-9 text-sm" data-testid="aniview-email" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Account ID</Label>
                <Input value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="Account ID" className="h-9 text-sm" data-testid="aniview-account-id" />
              </div>
              <div>
                <Label className="text-xs text-slate-600 mb-1 block">Password {settings.password_set && <span className="text-emerald-500 text-[10px]">(set)</span>}</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={settings.password_set ? "Leave blank to keep" : "Enter password"} className="h-9 text-sm" data-testid="aniview-password" />
              </div>
            </div>
          </details>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={saving} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="aniview-save-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Save Credentials
          </Button>
          <Button onClick={handleTest} disabled={testing} variant="outline" size="sm" data-testid="aniview-test-btn">
            {testing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plug size={14} className="mr-1.5" />} Test Connection
          </Button>
          {connected === true && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 size={14} /> Connected</span>}
          {connected === false && <span className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={14} /> Failed</span>}
        </div>
      </div>

      {/* Auto Sync */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="aniview-auto-sync">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-900">Daily Auto-Sync</h3>
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <Switch checked={dailySyncEnabled} onCheckedChange={setDailySyncEnabled} data-testid="aniview-auto-sync-toggle" />
            <span className="text-sm text-slate-600">{dailySyncEnabled ? "Enabled" : "Disabled"}</span>
          </div>
          {dailySyncEnabled && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Sync Time (UTC)</Label>
              <Input type="time" value={dailySyncTime} onChange={(e) => setDailySyncTime(e.target.value)} className="h-8 text-sm w-[120px]" data-testid="aniview-sync-time" />
            </div>
          )}
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <p className="text-[11px] text-slate-400">Automatically syncs yesterday's data daily.</p>
          <Button onClick={handleSave} disabled={saving} size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="aniview-save-sync-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Save Settings
          </Button>
        </div>
      </div>

      {/* Manual Sync */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="aniview-sync">
        <h3 className="text-sm font-semibold text-slate-900">Manual Sync</h3>
        <div className="flex items-center gap-3">
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">Start Date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9 text-sm w-[160px]" data-testid="aniview-start-date" />
          </div>
          <div>
            <Label className="text-xs text-slate-600 mb-1 block">End Date</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-9 text-sm w-[160px]" data-testid="aniview-end-date" />
          </div>
          <div className="pt-5">
            <Button onClick={handleSync} disabled={syncing || !startDate || !endDate} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="aniview-sync-btn">
              {syncing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Play size={14} className="mr-1.5" />} {syncing ? "Syncing..." : "Sync Now"}
            </Button>
          </div>
        </div>
      </div>

      {/* Sync Logs */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="aniview-logs">
        <div className="p-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900">Sync History</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Time</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Type</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Date Range</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4 text-right">Rows</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4 text-right">Duration</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400 text-sm">No sync history yet</TableCell></TableRow>
            ) : logs.map((log) => (
              <TableRow key={log.log_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <TableCell className="py-2.5 px-4 text-xs text-slate-500">{new Date(log.started_at).toLocaleString()}</TableCell>
                <TableCell className="py-2.5 px-4">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${log.sync_type === "manual" ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-600"}`}>
                    {log.sync_type}
                  </span>
                </TableCell>
                <TableCell className="py-2.5 px-4 text-xs text-slate-600">{log.start_date} → {log.end_date}</TableCell>
                <TableCell className="py-2.5 px-4">
                  {log.status === "success" ? (
                    <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 size={12} /> Success</span>
                  ) : log.status === "error" ? (
                    <span className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} /> Error</span>
                  ) : (
                    <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12} /> {log.status}</span>
                  )}
                </TableCell>
                <TableCell className="py-2.5 px-4 text-xs text-right text-slate-700">{log.rows_inserted + log.rows_updated}</TableCell>
                <TableCell className="py-2.5 px-4 text-xs text-right text-slate-500">{log.duration_seconds}s</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
